/**
 * useDashboardData Hook
 * Fetches and aggregates transaction data for the dashboard
 */

import { useState, useEffect, useRef } from 'react';
import { auth, db } from '@/config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { 
  getCurrentMonthTransactions,
  getTransactions,
  calculateSummary,
  getSpendingByCategory
} from '@/services/transactionService2';
import { getCategoryById as getOldCategoryById } from '@/constants/categoryTaxonomy';
import { findCategoryById as getUnifiedCategoryById } from '@/constants/unifiedCategories';
import { aggregateHistoricalSpending } from '@/utils/financialMetrics';
import { toTitleCase } from '@/lib/utils';

/**
 * Get category by ID - tries unified categories first, then falls back to legacy
 * @param {string} categoryId 
 * @returns {Object|null}
 */
function getCategoryById(categoryId) {
  if (!categoryId) return null;
  
  // Try unified categories first (new system)
  const unifiedCategory = getUnifiedCategoryById(categoryId);
  if (unifiedCategory) return unifiedCategory;
  
  // Fall back to legacy taxonomy
  const legacyCategory = getOldCategoryById(categoryId);
  if (legacyCategory) return legacyCategory;
  
  return null;
}

/**
 * Custom hook for dashboard data
 * @returns {Object} { loading, error, balance, income, expenses, categories, insightsData, refresh }
 */
export function useDashboardData() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [data, setData] = useState({
    balance: 0,
    income: 0,
    expenses: 0,
    categories: [],
    insightsData: [],
    historicalAnalytics: null
  });
  const hasCompletedInitialLoad = useRef(false);

  // Track auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        setData({
          balance: 0,
          income: 0,
          expenses: 0,
          categories: [],
          insightsData: [],
          historicalAnalytics: null
        });
        setLoading(false);
        hasCompletedInitialLoad.current = false;
      }
    });

    return () => unsubscribe();
  }, []);

  // Set up real-time listener for transactions
  useEffect(() => {
    if (!user) return;

    setLoading(true);
    hasCompletedInitialLoad.current = false;

    // Get current month's start date
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    // Listen to current month's transactions in real-time
    const transactionsRef = collection(db, 'users', user.uid, 'transactions');
    const q = query(
      transactionsRef,
      where('date', '>=', startOfMonth),
      orderBy('date', 'desc')
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        try {
          // Convert snapshot to transactions array
          const transactions = snapshot.docs.map(doc => {
            const data = doc.data();
            const normalizedType = (data.type || data.categoryType || data.transactionType || '').toLowerCase();
            return {
              id: doc.id,
              ...data,
              amount: Number(data.amount) || 0, // Ensure amount is always a number
              type: normalizedType,
              date: data.date?.toDate ? data.date.toDate() : data.date,
              createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
              updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt,
            };
          });

          // Calculate summary
          const summary = calculateSummary(transactions);

          // Get spending by category
          const spendingByCategory = getSpendingByCategory(transactions);

          // Build category cards with budgets
          const categoryCards = spendingByCategory.map(categoryData => {
            const category = getCategoryById(categoryData.categoryId);
            const budget = getBudgetForCategory(categoryData.categoryId);
            
            // Ensure amount is a number
            const amount = Number(categoryData.amount) || 0;
            
            // Format category name: use category name if found, otherwise format the ID or stored name
            let categoryName = category?.name || categoryData.categoryName || categoryData.categoryId || 'Unknown';
            // Convert kebab-case/snake_case IDs to Title Case (e.g., "groceries-food" -> "Groceries Food")
            if (!category?.name && categoryData.categoryId) {
              categoryName = toTitleCase(categoryData.categoryId.replace(/[-_]/g, ' '));
            }

            return {
              id: categoryData.categoryId,
              name: categoryName,
              amount: parseFloat(amount.toFixed(2)),
              icon: category?.icon || 'DollarSign',
              color: category?.color || '#6B7280',
              budget: budget
            };
          }).sort((a, b) => b.amount - a.amount);

          // Build insights data (top 5 categories)
          const insightsData = categoryCards.slice(0, 5).map(cat => ({
            category: cat.name,
            amount: cat.amount,
            color: cat.color
          }));

          setData(prevData => ({
            ...prevData,
            balance: summary.balance,
            income: summary.totalIncome,
            expenses: summary.totalExpenses,
            categories: categoryCards,
            insightsData: insightsData,
          }));

          setError(null);
        } catch (err) {
          console.error('Error processing transactions:', err);
          setError(err.message || 'Failed to process real-time data');
        } finally {
          if (!hasCompletedInitialLoad.current) {
            setLoading(false);
            hasCompletedInitialLoad.current = true;
          }
        }
      },
      (err) => {
        console.error('Error listening to transactions:', err);
        
        let errorMessage = 'Failed to load dashboard data';
        if (err.code === 'permission-denied') {
          errorMessage = 'You don\'t have permission to access this data. Please sign in again.';
        } else if (err.code === 'unavailable') {
          errorMessage = 'Unable to connect to the server. Please check your internet connection.';
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
        hasCompletedInitialLoad.current = true;
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Fetch historical data separately
  useEffect(() => {
    if (!user) return;

    const fetchHistoricalData = async () => {
      try {
        const historyStart = new Date();
        historyStart.setMonth(historyStart.getMonth() - 6);

        const historicalTransactions = await getTransactions(user.uid, {
          startDate: historyStart.toISOString(),
          endDate: new Date().toISOString()
        });

        const historicalAnalytics = aggregateHistoricalSpending(historicalTransactions, {
          weeks: 6,
          months: 6
        });

        setData(prevData => ({
          ...prevData,
          historicalAnalytics
        }));
      } catch (err) {
        console.error('Error fetching historical data:', err);
        // Non-critical error, so we don't set a top-level error state
      }
    };

    fetchHistoricalData();
  }, [user]);

  /**
   * Get budget for category
   * Returns null if no budget is set (user must explicitly set budgets)
   * TODO: Implement budget preferences service to fetch user-defined budgets
   * @param {String} categoryId
   * @returns {Number|null}
   */
  function getBudgetForCategory(categoryId) {
    // Return null - budgets should only show when user explicitly sets them
    // TODO: Fetch from user's budget preferences in Firestore
    return null;
  }

  /**
   * Refresh dashboard data (not needed with real-time listener, but kept for compatibility)
   */
  function refresh() {
    // Data refreshes automatically via real-time listener
    // This function is kept for compatibility but does nothing
  }

  return {
    loading,
    error,
    balance: data.balance,
    income: data.income,
    expenses: data.expenses,
    categories: data.categories,
    insightsData: data.insightsData,
    historicalAnalytics: data.historicalAnalytics,
    refresh
  };
}

export default useDashboardData;
